import * as React from 'react'
import {
  colors,
  CTAPrimary,
  CTASandUnderlined,
  OrangeUnderlinedLink
} from '@components/shared'
import { config } from 'react-awesome-styled-grid'
import {
  StyledInput,
  Form,
  ErrorMessage,
  ErrorMessageContainer
} from '@components/shared/formElements'
import styled from 'styled-components'
import Modal from '@components/shared/Modal'
import { isServer } from '../../env'
import { H1, P } from '@components/shared/typography'
import LoadingDotsAnimation from '@components/shared/LoadingDotsAnimation'
import { events } from '../../services/analytics'
import { useDispatch, useSelector } from 'react-redux'
import type { AppStateType } from '@reducers/appstate'
import { useRouter } from 'next/router'
import { saveBlogEmail } from '../../actions/userActions'
import BackgroundGradient from '@components/BackgroundGradient'

/*
  Shows if the user is:
    1. Not subscribed &&
    2. Didn't already dismiss it in the past 3 hours (checks local storage on browser) &&
    3. Hasn't already submitted their email (checks local storage on browser) &&
    4. Is client side

  Checks that user's email is a valid format and not already authenticated with
  firebase, before submitting event to iterable
*/

const DELAY_POP_TIME = 15000 // 30 seconds
const SNOOZE_TIME = 3 * 60 * 60 * 1000 // 3 hours

export default function BlogEmailCaptureModal (props: {delayTime?: number}): React.ReactNode {
  const dispatch = useDispatch()
  const isSubscribed = useSelector((state: AppStateType): boolean => !!state.user?.isSubscribed)
  const userId = useSelector((state: AppStateType): string | null | undefined => state.user?.id)
  const isPlayerActive = useSelector((state: AppStateType): boolean => !!state.currentPlaying)
  const blogEmailErrorText = useSelector((state: AppStateType): string | null | undefined => state.blogEmailErrorText)
  const blogEmailIsLoading = useSelector((state: AppStateType): boolean => state.blogEmailIsLoading)
  const blogEmailIsSuccess = useSelector((state: AppStateType): boolean => state.blogEmailIsSuccess)

  const router = useRouter()
  const blogEmailCaptureText = {
    intro: 'SHARE YOUR EMAIL',
    header: 'Get 30 Days Free',
    body: 'Unlock 1,000+ erotic audio stories and more.',
    submit: 'Get 30 days free',
    dismiss: 'Skip for now'
  }
  const blogEmailCaptureSuccessText = {
    intro: 'CHECK YOUR INBOX',
    header: 'Your Promo Is\nWaiting for You!',
    body: 'We sent you a special welcome email, plus an exclusive promo for 30 Days Free on the Dipsea app.',
    dismiss: 'close'
  }
  const { intro, header, body, submit, dismiss } = blogEmailCaptureText

  const [isVisible, setIsVisible] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(blogEmailIsLoading)
  const [errorMessage, setErrorMessage] = React.useState(blogEmailErrorText)
  const [email, setEmail] = React.useState('')

  const asPath = router.asPath || 'unknown'

  const isPopAvaliable = (): boolean => {
    try {
      if (isServer) {
        return false
      }
      if (isSubscribed || userId || isPlayerActive) {
        return false
      }
      if (window?.localStorage?.dipsea_blog_status) {
        if (window.localStorage?.dipsea_blog_status === 'success') {
          // email already saved, don't show modal
          return false
        } else if (window.localStorage?.dipsea_blog_status === 'snooze') {
          // was snoozed. If time is less than SNOOZE_TIME, don't show modal
          if (
            window?.localStorage?.dipsea_blog_date &&
            Date.now() - Number(window.localStorage.dipsea_blog_date) < SNOOZE_TIME
          ) {
            return false
          }
        }
      }
    } catch (e) {
      console.error('Failed to fetch local storage: dipsea_blog', e)
    }

    // can show modal
    return true
  }

  React.useEffect((): void => {
    if (!blogEmailIsSuccess && isPopAvaliable()) {
      setTimeout((): void => setIsVisible(true), props?.delayTime || DELAY_POP_TIME)
      events.blog_email_shown(asPath)
    }
  }, [isSubscribed, userId, isPlayerActive])

  React.useEffect((): void => {
    setIsLoading(blogEmailIsLoading)
  }, [blogEmailIsLoading])

  React.useEffect((): void => {
    setErrorMessage(blogEmailErrorText)
  }, [blogEmailErrorText])

  const saveToLocalStorage = (status: 'success' | 'snooze'): void => {
    try {
      window.localStorage.setItem(`dipsea_blog_status`, status)
      window.localStorage.setItem(`dipsea_blog_date`, String(Date.now()))
    } catch (e) {
      console.error('Failed to set to local storage: dipsea_blog', e)
    }
  }

  const isEmailRegex = (): boolean => {
    if (!email) return false
    const re = /\S+@\S+\.\S+/
    return re.test(email)
  }

  const handleEmailChange = (e: any): void => {
    const email = e.target.value.replace(/\s+/g, '')
    setEmail(email)
    setErrorMessage(undefined)
  }

  const handleSaveEmail = (e: any): void => {
    e.preventDefault()
    if (isLoading) {
      return
    }

    if (!email) {
      const errorText = 'Enter an email address to claim your promo'
      setErrorMessage(errorText)
    }

    if (!isEmailRegex()) {
      const errorText = 'There’s a typo in your email address'
      setErrorMessage(errorText)
      return
    }

    setIsLoading(true)
    const finalEmail = email.toLowerCase()
    dispatch(saveBlogEmail(finalEmail, asPath))
    saveToLocalStorage('success')
  }

  const handleCloseOnSuccess = (e?: KeyboardEvent): void => {
    if (e) e.preventDefault()
    setIsVisible(false)
  }

  const handleClose = (): void => {
    saveToLocalStorage('snooze')
    setIsVisible(false)
  }

  const RenderSuccessText = (): React.ReactNode => {
    const { intro, header, body, dismiss } = blogEmailCaptureSuccessText
    return (
      <TextContainer>
        <Intro>{intro}</Intro>
        <Header>{header}</Header>
        <Body>{body}</Body>
        <SuccessSkipButton onClick={handleCloseOnSuccess}>
          {dismiss}
        </SuccessSkipButton>
      </TextContainer>
    )
  }

  return (
    <PageContainer>
      <Modal
        isVisible={isVisible}
        toggleModalFunction={
          blogEmailIsSuccess ? handleCloseOnSuccess : handleClose
        }
        modalCardBackgroundColor={colors.cobalt}
        disableFullScreenMobile
        overflow='unset'
      >
        <BackgroundGradient overlayColor='blue' quadrant={3} />
        <Card>
          {blogEmailIsSuccess
            ? (
              <RenderSuccessText />
              )
            : (
              <TextContainer>
                <Intro>{intro}</Intro>
                <Header>{header}</Header>
                <Body>{body}</Body>

                <Form onSubmit={handleSaveEmail}>
                  <ErrorMessageContainer>
                    <ErrorMessage showError={!!errorMessage}>
                      {errorMessage || ''}
                    </ErrorMessage>
                  </ErrorMessageContainer>

                  <StyledInput
                    name='email'
                    type='email'
                    placeholder='Email'
                    className='blog-share-email'
                    value={email}
                    onChange={handleEmailChange}
                    valid={!errorMessage}
                    invertColor
                    styles={{ width: '100%' }}
                    color={colors.sand}
                    borderColor={colors.sand}
                  />

                  <SubmitButton disabled={isLoading}>
                    {isLoading
                      ? (
                        <LoadingDotsAnimation
                          color={colors.sand}
                          translucentColor={colors.sand20}
                        />
                        )
                      : (
                          submit
                        )}
                  </SubmitButton>
                </Form>

                <SkipButton onClick={handleClose}>{dismiss}</SkipButton>

                <PrivacyLink
                  target='_blank'
                  rel='noopener noreferrer'
                  href='https://www.dipseastories.com/privacypolicy/dark'
                >
                  Privacy Policy
                </PrivacyLink>
              </TextContainer>
              )}
        </Card>
      </Modal>
    </PageContainer>
  )
}

const SubmitButton = styled(CTAPrimary)<any>`
  margin: 31px 0 22px;
  min-height: 40px;
  padding: 10px;
  width: 100%;
  height: unset;
`
const SkipButton = styled(CTASandUnderlined)<any>`
  align-self: center;
  font-family: 'Good Sans Regular';
  font-weight: 500;
  margin: 0 0 30px;
`
const SuccessSkipButton = styled(CTASandUnderlined)<any>`
  align-self: center;
  font-family: 'Good Sans Regular';
  font-weight: 500;
  margin: 20px 0 0;
`
const PrivacyLink = styled(OrangeUnderlinedLink)<any>`
  align-self: center;
  font-family: 'Good Sans Regular';
  font-weight: 500;
  color: ${colors.orange};
`
const PageContainer = styled.div<any>`
  .modal-window {
    border-radius: 10px;
    max-width: 511px;
    ${(props: any): any => config(props).media.md`
      max-width: 594px;
    `}
  }
`
const Intro = styled(P)<any>`
  color: ${colors.sand};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.03em;
  line-height: 18px;
  margin: 0 0 12px;
  padding: 0;
  text-align: center;
  text-transform: uppercase;
`
const Header = styled(H1)<any>`
  color: ${colors.sand};
  font-size: 45px;
  line-height: 54px;
  margin: 0 0 16px;
  padding: 0;
  text-align: center;
  ${(props: any): any => config(props).media.md`
    white-space: pre-wrap
  `}
`
const Body = styled(P)<any>`
  color: ${colors.sand};
  font-size: 14px;
  font-weight: 300;
  line-height: 21px;
  margin: 0 0 10px;
  padding: 0;
  text-align: center;
`
const Card = styled.div<any>`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 56px 20px 38px;
  width: 100%;
  position: relative;
`
const TextContainer = styled.div<any>`
  display: flex;
  flex-direction: column;
  max-width: 332px;
  ${(props: any): any => config(props).media.md`
    max-width: 439px;
  `}
`
